import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import themeSettingsAPI from '../utils/api/themeSettingsAPI';
import {
  applyThemeSettings,
  cacheThemeSettings,
  getMergedThemeSettings,
  loadCachedThemeSettings
} from '../utils/themeRuntime';
import { DEFAULT_THEME_SETTINGS } from '../theme';
import './ManageTheme.css';

const colorGroups = [
  {
    title: 'Brand Colors',
    fields: [
      ['primary', 'Primary'],
      ['primaryDark', 'Primary Dark'],
      ['primaryLight', 'Primary Light'],
      ['secondary', 'Secondary'],
      ['secondaryDark', 'Secondary Dark'],
      ['secondaryLight', 'Secondary Light'],
      ['accent', 'Accent'],
      ['accentLight', 'Accent Light']
    ]
  },
  {
    title: 'Surfaces',
    fields: [
      ['bgPrimary', 'Background Primary'],
      ['bgSecondary', 'Background Secondary'],
      ['bgTertiary', 'Background Tertiary']
    ]
  },
  {
    title: 'Typography and States',
    fields: [
      ['textPrimary', 'Text Primary'],
      ['textSecondary', 'Text Secondary'],
      ['textTertiary', 'Text Tertiary'],
      ['success', 'Success'],
      ['warning', 'Warning'],
      ['danger', 'Danger'],
      ['info', 'Info']
    ]
  }
];

function ManageTheme() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(DEFAULT_THEME_SETTINGS);
  const [savedTheme, setSavedTheme] = useState(DEFAULT_THEME_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const meta = useMemo(
    () => [
      { label: 'Editable tokens', value: String(Object.keys(DEFAULT_THEME_SETTINGS).length) },
      { label: 'Primary', value: formData.primary },
      { label: 'Accent', value: formData.accent }
    ],
    [formData.accent, formData.primary]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadTheme() {
      try {
        setLoading(true);
        const response = await themeSettingsAPI.getAdminTheme();
        const nextTheme = getMergedThemeSettings(response.data || {});

        if (!isMounted) {
          return;
        }

        setFormData(nextTheme);
        setSavedTheme(nextTheme);
        applyThemeSettings(nextTheme);
        cacheThemeSettings(nextTheme);
      } catch (error) {
        const fallback = loadCachedThemeSettings();
        if (!isMounted) {
          return;
        }

        setFormData(fallback);
        setSavedTheme(fallback);
        applyThemeSettings(fallback);
        showToast('❌ Theme settings load nahi ho saki', 'error');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadTheme();

    return () => {
      isMounted = false;
    };
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3000);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (event.target.type === 'text') {
      const normalized = value.startsWith('#') ? value.toUpperCase() : `#${value.toUpperCase()}`;
      if (!/^#([0-9A-F]{6})$/.test(normalized)) {
        return;
      }

      setFormData((current) => ({
        ...current,
        [name]: normalized
      }));
      return;
    }

    setFormData((current) => ({
      ...current,
      [name]: value.toUpperCase()
    }));
  };

  const handlePreview = () => {
    applyThemeSettings(formData);
    showToast('✅ Live preview apply ho gaya');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await themeSettingsAPI.updateTheme(formData);
      const nextTheme = getMergedThemeSettings(response.data || formData);

      setSavedTheme(nextTheme);
      setFormData(nextTheme);
      applyThemeSettings(nextTheme);
      cacheThemeSettings(nextTheme);
      showToast('✅ Theme settings save ho gayi');
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      const response = await themeSettingsAPI.resetTheme();
      const nextTheme = getMergedThemeSettings(response.data || DEFAULT_THEME_SETTINGS);

      setSavedTheme(nextTheme);
      setFormData(nextTheme);
      applyThemeSettings(nextTheme);
      cacheThemeSettings(nextTheme);
      showToast('✅ Theme defaults restore ho gaye');
    } catch (error) {
      showToast(`❌ ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setFormData(savedTheme);
    applyThemeSettings(savedTheme);
    showToast('✅ Saved theme dubara apply ho gaya');
  };

  return (
    <>
      {toast ? <div className={`theme-toast ${toast.type}`}>{toast.message}</div> : null}

      <AdminShell
        eyebrow="Brand System"
        title="Manage website theme"
        description="Control the major global colors for the full website from one admin route and persist them across refreshes."
        primaryAction={{ label: saving ? 'Saving...' : 'Save theme', onClick: handleSave }}
        secondaryAction={{ label: 'Dashboard', onClick: () => navigate('/admin/dashboard') }}
        meta={meta}
      >
        <div className="manage-theme-page">
          <section className="theme-preview-card">
            <div className="theme-preview-header">
              <div>
                <span className="theme-section-kicker">Live preview</span>
                <h3>Theme snapshot</h3>
                <p>Preview exactly how the current theme palette behaves before saving.</p>
              </div>

              <div className="theme-preview-actions">
                <button type="button" className="theme-btn secondary" onClick={handlePreview} disabled={loading || saving}>
                  Preview
                </button>
                <button type="button" className="theme-btn secondary" onClick={handleDiscard} disabled={loading || saving}>
                  Discard changes
                </button>
                <button type="button" className="theme-btn danger" onClick={handleReset} disabled={loading || saving}>
                  Reset default
                </button>
              </div>
            </div>

            <div className="theme-preview-showcase">
              <div className="theme-preview-hero" style={{ background: `linear-gradient(135deg, ${formData.primary} 0%, ${formData.secondary} 100%)` }}>
                <span className="theme-preview-badge" style={{ backgroundColor: formData.accent, color: formData.textPrimary }}>Theme Preview</span>
                <h4 style={{ color: formData.bgPrimary }}>LAATH SAAB News</h4>
                <p style={{ color: formData.bgPrimary }}>Admin controlled visual identity for your complete website.</p>
              </div>

              <div className="theme-preview-surface" style={{ backgroundColor: formData.bgPrimary }}>
                <div className="theme-swatch-grid">
                  {Object.entries(DEFAULT_THEME_SETTINGS).map(([key]) => (
                    <div key={key} className="theme-swatch-card">
                      <span className="theme-swatch-color" style={{ backgroundColor: formData[key] }} />
                      <div>
                        <strong>{key}</strong>
                        <small>{formData[key]}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <div className="theme-groups-grid">
            {colorGroups.map((group) => (
              <section key={group.title} className="theme-group-card">
                <div className="theme-group-heading">
                  <h3>{group.title}</h3>
                  <p>{group.fields.length} editable settings</p>
                </div>

                <div className="theme-field-grid">
                  {group.fields.map(([name, label]) => (
                    <label key={name} className="theme-field">
                      <span>{label}</span>
                      <div className="theme-input-row">
                        <input type="color" name={name} value={formData[name]} onChange={handleInputChange} disabled={loading || saving} />
                        <input type="text" name={name} value={formData[name]} onChange={handleInputChange} disabled={loading || saving} maxLength={7} />
                      </div>
                    </label>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </AdminShell>
    </>
  );
}

export default ManageTheme;